import locationData from "../../data/location-data";
import { DIACRITICS_MAP } from "./location-mapping.constants";
import type { ProcessedRow, LocationDataRow } from "./location-mapping.types";

/** Memoization cache — each unique string is processed at most once for performance. */

/**
 * normalizeCache has format Map<originalString, normalizedString> and stores the results of normalizeText()
 * Example:
 * {
 *     "Cần Đăng": "candang",
 *     "Phú Quốc": "phuquoc",
 *     "R&D": "rand",
 *     ...
 * }
 */
const normalizeCache = new Map<string, string>();

/**
 * Normalize a string for search comparison.
 *
 * Examples:
 *   "Xã Cà Nàng"  → "xacanang"
 *   "Phú Quốc"    → "phuquoc"
 *   "R&D"         → "rand"
 */
export function normalizeText(text: string): string {
  if (!text) return "";

  const cached = normalizeCache.get(text);
  if (cached !== undefined) return cached;

  const result = text
    // Step 1 – diacritics removal → base Latin: "Cần Đăng" → "Can Dang"
    .replace(/[^\u0020-\u007E]/g, (ch) => DIACRITICS_MAP[ch] ?? ch)
    // Step 2 – lowercase: "Can Dang" → "can dang"
    .toLowerCase()
    // Step 3 – ampersand: "R&D" → "R and D"
    .replace(/&/g, "and")
    // Step 4 – strip non-letter / non-digit: "can dang" → "candang", "R and D" → "rand"
    .replace(/[^\p{Letter}\p{Number}]/gu, "");

  normalizeCache.set(text, result);
  return result;
}

/**
 * The entire dataset pre-processed into a more convenient format for filtering.
 * Exmples:
 *   original.columns: ["An Giang", "Cần Đăng", "Xã Vĩnh Lợi, Xã Cần Đăng"]
 *   normalized: ["angiang", "candang", "xavinhloicandang"]
 *
 *  Input - raw location data:
 *   [
 *     { columns: ["An Giang", "Cần Đăng", "Xã Vĩnh Lợi, Xã Cần Đăng"] },
 *    ...
 *   ]
 *
 *  Output - allRows:
 *   [
 *     {
 *       original: { columns: ["An Giang", "Cần Đăng", "Xã Vĩnh Lợi, Xã Cần Đăng"] },
 *       normalized: ["angiang", "candang", "xavinhloicandang"]
 *     }, ...
 *   ]
 */
export const allRows: ProcessedRow[] = (locationData as { rows: LocationDataRow[] }).rows.map(
  (row, index) => ({
    id: index,
    original: row,
    normalized: [
      normalizeText(row.columns[0]),
      normalizeText(row.columns[1]),
      normalizeText(row.columns[2]),
    ],
  }),
);
